import { useCallback, useMemo } from 'react';
import { useCentralizedProviderData } from '@crehana/auth';

// TODO: Remove this and use the one from @crehana/rbac
/**
 * Custom hook to check if the user has specific permissions
 * Copy-pasted from @crehana/rbac package for use in hana-ai
 */

// ===== TYPES =====

/**
 * Hook to check user permissions and product access
 * Provides utilities for role-based access control
 *
 * @returns Object containing permission checking functions and user data
 */
export var useHasPermissions = function useHasPermissions() {
  var _me$security3, _me$security3$applica, _me$security4, _me$security4$applica, _me$security5, _me$security5$applica;
  var _useCentralizedProvid = useCentralizedProviderData(),
    me = _useCentralizedProvid.me;

  // Memoized user data
  var applicationProducts = useMemo(function () {
    var _me$security;
    return (me === null || me === void 0 ? void 0 : (_me$security = me.security) === null || _me$security === void 0 ? void 0 : _me$security.applicationProducts) || [];
  }, [me]);
  var applicationProductsSet = useMemo(function () {
    return new Set(applicationProducts);
  }, [applicationProducts]);
  var applicationPermissions = useMemo(function () {
    var _me$security2;
    return (me === null || me === void 0 ? void 0 : (_me$security2 = me.security) === null || _me$security2 === void 0 ? void 0 : _me$security2.applicationPermissions) || [];
  }, [me]);
  var applicationPermissionsLength = useMemo(function () {
    return applicationPermissions.length;
  }, [applicationPermissions]);
  var applicationPermissionsSet = useMemo(function () {
    return new Set(applicationPermissions);
  }, [applicationPermissions]);

  // Role checks
  var isAdminUser = Boolean(me === null || me === void 0 ? void 0 : (_me$security3 = me.security) === null || _me$security3 === void 0 ? void 0 : (_me$security3$applica = _me$security3.applicationRoles) === null || _me$security3$applica === void 0 ? void 0 : _me$security3$applica.includes('ROLE_ADMIN'));
  var isUser = Boolean(me === null || me === void 0 ? void 0 : (_me$security4 = me.security) === null || _me$security4 === void 0 ? void 0 : (_me$security4$applica = _me$security4.applicationRoles) === null || _me$security4$applica === void 0 ? void 0 : _me$security4$applica.includes('ROLE_USER'));

  // Check if user is super admin based on roles
  var isSuperAdminUser = Boolean(me === null || me === void 0 ? void 0 : (_me$security5 = me.security) === null || _me$security5 === void 0 ? void 0 : (_me$security5$applica = _me$security5.applicationRoles) === null || _me$security5$applica === void 0 ? void 0 : _me$security5$applica.includes('ROLE_SUPERADMIN'));

  // Product access check function
  var hasProducts = useCallback(function (products, _ref) {
    var operator = _ref.operator;
    if (operator === 'AND') {
      return products.every(function (product) {
        return applicationProductsSet.has(product);
      });
    }
    return products.some(function (product) {
      return applicationProductsSet.has(product);
    });
  }, [applicationProductsSet]);

  // Permission check function
  var hasPermissions = useCallback(function (permissions, _ref2) {
    var operator = _ref2.operator;
    if (
    /**
     * @security When applicationPermissions is empty, this grants universal access.
     * This typically indicates a super-admin or unrestricted account.
     */
    applicationPermissionsLength === 0 ||
    /**
     * @security When user has the SUPERADMIN role, this grants universal access.
     */
    isSuperAdminUser || isAdminUser) {
      return true;
    }
    if (operator === 'AND') {
      return permissions.every(function (permission) {
        return applicationPermissionsSet.has(permission);
      });
    }
    return permissions.some(function (permission) {
      return applicationPermissionsSet.has(permission);
    });
  }, [applicationPermissionsLength, applicationPermissionsSet, isSuperAdminUser, isAdminUser]);

  // Product access helpers
  var hasHanaAccess = useCallback(function () {
    return hasProducts(['HANA'], {
      operator: 'AND'
    });
  }, [hasProducts]);
  return {
    hasProducts: hasProducts,
    hasPermissions: hasPermissions,
    hasHanaAccess: hasHanaAccess,
    applicationProducts: applicationProducts,
    applicationPermissions: applicationPermissions,
    isUser: isUser,
    isSuperAdminUser: isSuperAdminUser,
    isAdminUser: isAdminUser
  };
};